import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useMMKVObject, useMMKVString } from 'react-native-mmkv';

import { checkoutCartMutation } from '@/api/booking/checkoutCartMutation';
import type { CheckoutCartInput } from '@/api/booking/checkoutCartMutation/types';
import type {
  CartCustomerWithLanguage,
  UpsertCartResponse,
} from '@/api/booking/upsertCartMutation/types';
import { customerStateQueryOptions } from '@/api/customer/customerStateQuery';
import type { GuestUserInfo } from '@/components/_shared/UserInformationForm/types';
import { storage, storageKeys } from '@/constants/storage';
import { useAppStoreReviewContext } from '@/context/appStoreReview';
import { useNPSSurveyContext } from '@/context/nps';
import { getAttributionTrackingParams } from '@/utils/attribution';
import { getBookingPlatform } from '@/utils/bookingAttribution';
import { classifyCheckoutError } from '@/utils/checkoutErrors';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import {
  ANY_KEY_SEGMENT,
  invalidateQueriesByKeyPattern,
} from '@/utils/queryInvalidation';
import { buildRwgConversion, clearRwg, reportRwgConversion } from '@/utils/rwg';
import { adoptCheckoutCustomerId } from '@/utils/sessionUtils';
import { Tracker, trackBookAppointment } from '@/utils/tracking';
import { DECLINE_REASON_UNKNOWN } from '@/utils/tracking/ldEvents';
import {
  trackLdPaymentAttempted,
  trackLdPaymentResult,
} from '@/utils/tracking/trackLdCheckoutEvents';

interface UseCheckoutSideEffectsOptions {
  shopId: string;
  cartId: string;
  /**
   * The shop's route slug, when the caller has it. `shopId` is a GUID on the
   * main booking path but a slug on the Cash App return path, and a Reserve
   * with Google landing URL only ever carries the slug — so both are needed
   * to tell whether the customer switched shops. SQR-16105.
   */
  shopRoute?: string;
  /**
   * The booking brand's GA4 id (`brand.settings.gaMeasurementId`). Without it
   * native skips the conversion, so it can't leak to another brand (SQR-15581).
   */
  shopGaMeasurementId?: string | null;
  /** The booking brand's Meta pixel id (`brand.settings.metaPixelId`). */
  shopMetaPixelId?: string | null;
  customer: CartCustomerWithLanguage | undefined;
  onSuccess?: (data: UpsertCartResponse) => void;
  onError?: (error: Error) => void;
}

export const useCheckoutSideEffects = ({
  shopId,
  cartId,
  shopRoute,
  shopGaMeasurementId,
  shopMetaPixelId,
  customer,
  onSuccess,
  onError,
}: UseCheckoutSideEffectsOptions) => {
  const queryClient = useQueryClient();
  const { triggerNPSCheck } = useNPSSurveyContext();
  const { triggerAppStoreReviewCheck } = useAppStoreReviewContext();
  const [existingCustomerId] = useMMKVString(storageKeys.customerId, storage);
  const [, setGuestUserInfo] = useMMKVObject<GuestUserInfo>(
    storageKeys.guestUserInfo,
    storage,
  );

  const wasLoggedInAtStartRef = useRef(!!existingCustomerId);

  const persistGuestUserInfo = useCallback(() => {
    if (!wasLoggedInAtStartRef.current && customer) {
      setGuestUserInfo(customer);
    }
  }, [customer, setGuestUserInfo]);

  const handleCheckoutSuccess = useCallback(
    (checkoutData: UpsertCartResponse) => {
      const customerId = adoptCheckoutCustomerId(
        checkoutData.payingCustomer?.id,
      );

      const confirmationCode = checkoutData.items.find(
        (i) => i.appointment.confirmationCode,
      )?.appointment.confirmationCode;

      if (confirmationCode) {
        storage.set(storageKeys.appointmentConfirmationCode, confirmationCode);
      }

      persistGuestUserInfo();

      // Match by key shape rather than gating on a resolved customerId - the
      // checkout response doesn't always carry `payingCustomer` for an
      // already-logged-in customer, and the id on disk isn't guaranteed
      // either, so requiring one here would silently skip this invalidation
      // exactly like the bug this fixes (SQR-15876).
      invalidateQueriesByKeyPattern(
        queryClient,
        customerStateQueryOptions(ANY_KEY_SEGMENT).queryKey,
      );

      // These, unlike the invalidation above, genuinely need a resolved
      // customer id - skip them rather than firing for an unknown customer.
      if (customerId) {
        // NPSSurveyProvider arbitrates against the review check via
        // `reviewCheckStatus`, so both can be armed unconditionally here.
        void triggerAppStoreReviewCheck(customerId);
        triggerNPSCheck(customerId);
      }
    },
    [
      persistGuestUserInfo,
      queryClient,
      triggerNPSCheck,
      triggerAppStoreReviewCheck,
    ],
  );

  const {
    mutate: mutateCheckoutCart,
    mutateAsync: mutateCheckoutCartAsync,
    isPending: isCheckingOutCart,
    isError: isCheckoutError,
    error: checkoutCartError,
    reset: resetCheckout,
  } = useMutation(
    checkoutCartMutation(shopId, cartId, {
      // `checkoutCartMutation` pins `retry: false`, so this runs exactly once
      // per attempt — one `ld-payment-attempted` per charge handed to the API.
      onMutate: () => {
        trackLdPaymentAttempted(cartId);
      },
      onSuccess: (checkoutData) => {
        Tracker.track('booking_completed', {
          shopId,
          saleOrderId: checkoutData.id || '',
          customerId: checkoutData.payingCustomer?.id || '',
          appointmentCount: checkoutData.items.length.toString(),
          wasLoggedIn: wasLoggedInAtStartRef.current.toString(),
          platform: getBookingPlatform(),
          totalDollars: String((checkoutData.total ?? 0) / 100),
          currency: checkoutData.currency,
          shopName: checkoutData.shop?.name ?? '',
          // Google Ads attribution captured on deep-link entry (Phase 1). Sent
          // on the internal event so RudderStack et al. can tie a booking to
          // its ad click — the GA `book_appointment` conversion carries the
          // same params separately via the GA tracker.
          ...getAttributionTrackingParams(),
        });
        trackBookAppointment({
          saleOrderId: checkoutData.id || '',
          totalDollars: (checkoutData.total ?? 0) / 100,
          currency: checkoutData.currency,
          shopId,
          shopName: checkoutData.shop?.name,
          gaMeasurementId: shopGaMeasurementId,
          metaPixelId: shopMetaPixelId,
        });

        // A Reserve with Google booking is reported to Google exactly once, at
        // the moment payment succeeds, and the token is spent so a later booking
        // in the same session can never re-report it.
        //
        // Web only: `reportRwgConversion` is a no-op on native, so clearing the
        // token there would discard it without ever reporting. The root layout
        // captures it on native too, so it survives for SQR-16104. SQR-16105.
        if (isWeb()) {
          const rwgConversion = buildRwgConversion({
            id: shopId,
            route: shopRoute,
          });
          if (rwgConversion) {
            reportRwgConversion(rwgConversion);
            clearRwg();
          }
        }
        trackLdPaymentResult(cartId, 'success', DECLINE_REASON_UNKNOWN);
        handleCheckoutSuccess(checkoutData);
        onSuccess?.(checkoutData);
      },
      onError: (error) => {
        const errorType = classifyCheckoutError(error);
        const { code } = (error ?? {}) as { code?: unknown };
        const failure = { cartId, errorType, code };
        // A timeout leaves the charge's outcome unknown, so it reports as an
        // error; a decline or an offline device is the customer's outcome.
        if (errorType === 'payment') {
          Logger.warn('Checkout declined', failure);
        } else if (errorType === 'network') {
          Logger.warn('Checkout failed while offline', failure);
        } else {
          Logger.error('Checkout failed', failure);
        }

        Tracker.track('booking_failed', {
          shopId,
          cartId,
          error: String(error),
          errorType,
        });
        // Only the coarse classification bucket ever becomes `decline_reason`:
        // card declines arrive as a bare 402 with no typed body, and this value
        // ships to a third party, so `error.message` or raw response data could
        // leak a customer email, id or card detail.
        trackLdPaymentResult(
          cartId,
          errorType === 'payment' ? 'declined' : 'error',
          errorType,
        );
        onError?.(error);
      },
    }),
  );

  // The platform is resolved here, not by callers. Checkout happens from the
  // booking flow and from each payment method's own return screen, and every
  // one of them previously passed its own `platform` — one of them silently
  // kept sending the device platform and lost its Reserve with Google
  // attribution. Injecting it makes that unrepresentable. SQR-16105.
  const checkoutCart = useCallback(
    (input: CheckoutCartInput) =>
      mutateCheckoutCart({ ...input, platform: getBookingPlatform() }),
    [mutateCheckoutCart],
  );

  const checkoutCartAsync = useCallback(
    (input: CheckoutCartInput) =>
      mutateCheckoutCartAsync({ ...input, platform: getBookingPlatform() }),
    [mutateCheckoutCartAsync],
  );

  return {
    checkoutCart,
    checkoutCartAsync,
    isCheckingOutCart,
    isCheckoutError,
    checkoutCartError,
    resetCheckout,
    handleCheckoutSuccess,
    persistGuestUserInfo,
    wasLoggedInAtStart: wasLoggedInAtStartRef,
  };
};
